import { BoardPreview, IBoard, IBoardStyle, Spacer, createBoard } from '@vestaboard/installables'
import { Box, Button, Checkbox, CircularProgress, Snackbar, Tooltip, Typography } from '@material-ui/core'
import React, { useEffect, useState, useRef } from 'react'
import W3CWebSocket, { CloseEvent } from 'reconnecting-websocket'

import { Alert } from '@material-ui/lab'
import MuiAlert from '@material-ui/lab/Alert'
import { Link } from '../../link'
import { useBoardDetailQuery } from '../hooks/useBoardDetailQuery'
import { useGenerateWebsocketAuthToken } from '../../../api/useGenerateWebsocketAuthToken'
import { useEnableDeviceAcks, useDisableDeviceAcks } from '../../../api/useDeviceAcks'
import { useParams } from 'react-router'
import { v4 as uuid } from 'uuid'
import { vbml } from '@vestaboard/vbml'

import { WS_BASE_URL } from '../../../env'

enum EDeviceMessageType {
  DisplayCommand = 'DeviceMessageDisplayCommand',
  Heartbeat = 'DeviceHeartbeatMessage',
  VerboseHeartBeat = 'DeviceHeartbeatMessage',
  BoardLayoutChanged = 'DeviceBoardLayoutChangedMessage',
  Refresh = 'DeviceRefreshCommand',
  Identify = 'DeviceIdentifyMessage'
}

const constructDeviceMessage = (deviceId: string, type: EDeviceMessageType, payload: {}) => {
  return JSON.stringify({
    message: {
      id: uuid(),
      type,
      deviceId,
      clientTime: new Date().getTime(),
      maskStatus: 'On',
      ...payload
    }
  })
}

const constructVerboseHeartbeatMessage = (deviceId: string) => {
  return constructDeviceMessage(deviceId, EDeviceMessageType.VerboseHeartBeat, {
    created: new Date().getTime(),
    networkInterfaces: [
      {
        name: 'wlan0',
        iface: 'wlan0',
        isUp: true,
        mtu: 100,
        inetAddresses: [''],
        interfaceAddresses: [''],
        isLoopback: false,
        isVirtual: false,
        index: 0,
        hardwareAddress: '00:00:00:00'
      }
    ],
    currentNetwork: {
      ssid: 'ssid-simulator-heartbeat',
      bssid: 'bssid-simulator-heartbeat',
      freq: 0,
      ipAddress: '127.0.0.1',
      isUp: true
    },
    diagnostics: {
      networkInterfaces: [
        {
          name: 'wlan0',
          iface: 'wlan0',
          isUp: true,
          mtu: 100,
          inetAddresses: [''],
          interfaceAddresses: [''],
          isLoopback: false,
          isVirtual: false,
          index: 0,
          hardwareAddress: '00:00:00:00'
        }
      ],
      currentNetwork: {
        ssid: 'ssid-simulator-heartbeat',
        bssid: 'bssid-simulator-heartbeat',
        freq: 0,
        ipAddress: '127.0.0.1',
        isUp: true
      }
    }
  })
}

const constructHeartbeatMessage = (deviceId: string) => {
  return constructDeviceMessage(deviceId, EDeviceMessageType.Heartbeat, {
    created: new Date().getTime()
  })
}

const constructIdentifyMessage = (deviceId: string, boardId: string) => {
  return constructDeviceMessage(deviceId, EDeviceMessageType.Identify, { boardId })
}

export const LiveBoard = () => {
  const { id: boardId } = useParams<{ id: string }>()
  const { data, error, loading } = useBoardDetailQuery(boardId)
  const [generateAuthToken] = useGenerateWebsocketAuthToken()
  const [enableAcks] = useEnableDeviceAcks()
  const [disableAcks] = useDisableDeviceAcks()
  const [ackLoading, setAckLoading] = useState(false)
  const [ackMessage, setAckMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null)
  const [sendVerboseHeartbeats, setSendVerboseHeartbeats] = useState<boolean>(false)
  const [sendHeartbeats, setSendHeartbeats] = useState<boolean>(false)
  const [showWebsocketConnectEvents, setShowWebsocketConnectEvents] = useState<boolean>(true)
  const [showAllLogs, setShowAllLogs] = useState<boolean>(false)
  const [viewBoardStatus, setViewBoardStatus] = useState<boolean>(false)
  const [boardStatus, setBoardStatus] = useState<any>([])

  // Use refs for values that shouldn't trigger reconnection
  const sendVerboseHeartbeatsRef = useRef(sendVerboseHeartbeats)
  const sendHeartbeatsRef = useRef(sendHeartbeats)
  const showWebsocketConnectEventsRef = useRef(showWebsocketConnectEvents)
  const showAllLogsRef = useRef(showAllLogs)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const verboseHeartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const clientRef = useRef<W3CWebSocket | null>(null)
  const displayAtTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Keep refs in sync with state, and send immediately when toggled on
  useEffect(() => {
    sendVerboseHeartbeatsRef.current = sendVerboseHeartbeats
    if (sendVerboseHeartbeats && clientRef.current && clientRef.current.readyState === WebSocket.OPEN) {
      const deviceId = data?.board?.devices?.[0]?.id
      if (deviceId) {
        clientRef.current.send(constructVerboseHeartbeatMessage(deviceId))
      }
    }
  }, [sendVerboseHeartbeats, data?.board?.devices])

  useEffect(() => {
    sendHeartbeatsRef.current = sendHeartbeats
    if (sendHeartbeats && clientRef.current && clientRef.current.readyState === WebSocket.OPEN) {
      const deviceId = data?.board?.devices?.[0]?.id
      if (deviceId) {
        clientRef.current.send(constructHeartbeatMessage(deviceId))
      }
    }
  }, [sendHeartbeats, data?.board?.devices])

  useEffect(() => {
    showWebsocketConnectEventsRef.current = showWebsocketConnectEvents
  }, [showWebsocketConnectEvents])

  useEffect(() => {
    showAllLogsRef.current = showAllLogs
  }, [showAllLogs])

  const [message, setMessage] = useState<Array<number[]>>(createBoard())
  const [messages, setMessages] = useState<
    Array<{
      messageId: string
      message: string
      time: string
      serverTime: string
      created?: string
      displayAt?: string
      delay: number
      otherEvent: boolean
      transition?: any
    }>
  >()


  useEffect(() => {
    if (boardId && viewBoardStatus) {
      const statusUrl = `${WS_BASE_URL?.replace('/ws', '/board-status')}/${boardId}`
      const clientBoardStatus = new W3CWebSocket(statusUrl, undefined, {
        maxReconnectionDelay: 5000,
        minReconnectionDelay: 1000,
        reconnectionDelayGrowFactor: 1.3,
        connectionTimeout: 5000,
        maxRetries: 10,
        debug: false
      })

      const onConnect = () => {
        setBoardStatus((prev: any) => [`connected ${statusUrl}`, ...prev])
      }
      const handleMessage = (message: any) => {
        setBoardStatus((prev: any) => [message, ...prev])
      }
      clientBoardStatus.onopen = () => {
        onConnect()
      }
      clientBoardStatus.onmessage = (message: any) => {
        handleMessage(message.data)
      }
      return () => {
        clientBoardStatus.close()
      }
    }
  }, [boardId, viewBoardStatus])

  // Extract device fields as stable values to prevent unnecessary reconnections
  const deviceId = data?.board?.devices?.[0]?.id
  const pairingCode = data?.board?.devices?.[0]?.pairingCode

  useEffect(() => {
    if (!deviceId || !pairingCode) {
      return
    }

    // Prevent duplicate connections
    if (clientRef.current) {
      return
    }

    if (boardId) {
      // Async URL provider generates a fresh auth token on every connect/reconnect
      const urlProvider = async () => {
        const result = await generateAuthToken({
          variables: { deviceId, pairingCode }
        })
        const token = result.data?.generateWebsocketAuthTokenV2
        if (!token) {
          throw new Error('Failed to generate WebSocket auth token')
        }
        return `${WS_BASE_URL}/${deviceId}?X-Vestaboard-Api-Auth=${token}`
      }

      const client = new W3CWebSocket(
        urlProvider,
        undefined,
        {
          maxReconnectionDelay: 5000,
          minReconnectionDelay: 1000,
          reconnectionDelayGrowFactor: 1.3,
          connectionTimeout: 5000,
          maxRetries: 10,
          debug: false
        }
      )

      clientRef.current = client

      const sendVerboseHeartbeatMessage = () => {
        const message = constructVerboseHeartbeatMessage(deviceId)
        if (sendVerboseHeartbeatsRef.current) {
          client.send(message)
        }
      }

      const sendHeartbeat = () => {
        const message = constructHeartbeatMessage(deviceId)
        if (sendHeartbeatsRef.current) {
          client.send(message)
        }
      }

      const sendIdentify = () => {
        const message = constructIdentifyMessage(deviceId, boardId)
        client.send(message)
      }

      const onConnect = () => {
        sendHeartbeat()
        sendVerboseHeartbeatMessage()
        sendIdentify()

        // Clear any existing intervals before setting new ones
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
        if (verboseHeartbeatIntervalRef.current) {
          clearInterval(verboseHeartbeatIntervalRef.current)
        }

        // Use setInterval instead of recursive setTimeout
        heartbeatIntervalRef.current = setInterval(() => {
          sendHeartbeat()
        }, 60 * 1000)

        verboseHeartbeatIntervalRef.current = setInterval(() => {
          sendVerboseHeartbeatMessage()
        }, 120 * 1000)

        if (showWebsocketConnectEventsRef.current || showAllLogsRef.current) {
          setMessages(prev => {
            return [
              {
                messageId: 'Connected Websocket',
                message: 'Websocket reconnected and message hydrating',
                time: new Date().toLocaleString() + ' millis:' + new Date().getTime(),
                serverTime: new Date().toLocaleString() + ' millis:' + new Date().getTime(),
                delay: 0,
                otherEvent: true
              },
              ...(prev || [])
            ]
          })
        }
      }

      const handleMessage = (message: any) => {
        const json = JSON.parse(message)

        switch (json.message.type) {
          case EDeviceMessageType.DisplayCommand:
            const applyDisplay = () => setMessage(json.message.characters.characters)
            if (json.message.displayAt) {
              const delayMs = json.message.displayAt - Date.now()
              if (displayAtTimeoutRef.current) clearTimeout(displayAtTimeoutRef.current)
              if (delayMs > 0) {
                displayAtTimeoutRef.current = setTimeout(applyDisplay, delayMs)
              } else {
                applyDisplay()
              }
            } else {
              applyDisplay()
            }
            setMessages(prev => {
              // remove this check if you want to see duplicate hydration messages
              if (prev?.[0]?.messageId === json?.message?.messageId) {
                return prev
              }
              return [
                {
                  messageId: `${json?.message?.type} - ${json?.message?.messageId}`,
                  message: vbml.characterCodesToAscii(json.message.characters.characters),
                  time: new Date().toLocaleString() + ' millis:' + new Date().getTime(),
                  created: new Date(json.message.created).toLocaleString() + ' millis:' + json.message.created,
                  serverTime: new Date(json.message.serverTime).toLocaleString() + ' millis:' + json.message.serverTime,
                  displayAt: json.message.displayAt
                    ? new Date(json.message.displayAt).toLocaleString() + '.' + String(json.message.displayAt % 1000).padStart(3, '0') + ' millis:' + json.message.displayAt
                    : undefined,
                  delay: json.message.serverTime - new Date().getTime(),
                  otherEvent: false,
                  transition: json?.message?.transition
                },
                ...(prev || [])
              ]
            })
            break
          default:
            if (showAllLogsRef.current) {
              setMessages(prev => {
                return [
                  {
                    messageId: json.message.type,
                    message: JSON.stringify(json?.message) + '\n',
                    time: new Date().toLocaleString() + ' millis:' + new Date().getTime(),
                    serverTime:
                      new Date(json.message.serverTime).toLocaleString() + ' millis:' + json.message.serverTime,
                    delay: json.message.serverTime - new Date().getTime(),
                    otherEvent: true,
                    transition: json?.message?.transition
                  },
                  ...(prev || [])
                ]
              })
            }
            break
        }
      }

      client.onopen = () => {
        onConnect()
      }

      client.onmessage = (message: any) => {
        handleMessage(message.data)
      }

      client.onclose = (event: CloseEvent) => {
        // With fresh auth tokens on each reconnect, let the library handle all reconnections
        console.warn(`WebSocket closed with code ${event.code} - will reconnect with fresh token`)
      }

      return () => {
        // Clean up intervals
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
          heartbeatIntervalRef.current = null
        }
        if (verboseHeartbeatIntervalRef.current) {
          clearInterval(verboseHeartbeatIntervalRef.current)
          verboseHeartbeatIntervalRef.current = null
        }
        if (displayAtTimeoutRef.current) {
          clearTimeout(displayAtTimeoutRef.current)
          displayAtTimeoutRef.current = null
        }
        clientRef.current = null
        client.close()
      }
    }
  }, [boardId, deviceId, pairingCode, generateAuthToken])

  return error ? (
    <Alert severity='error'>{error.message || 'Board not found'}</Alert>
  ) : !data || loading ? (
    <CircularProgress />
  ) : (
    <>
      <Typography variant='button' display='block' gutterBottom>
        <Link to={`/boards`}>Board</Link> | <Link to={`/boards/${boardId}`}>{data.board.title}</Link>
      </Typography>
      <Spacer size='extraLarge' />
      <Box
        style={{
          maxWidth: 600
        }}>
        <BoardPreview characters={message as IBoard} boardStyle={data?.board?.boardStyle as IBoardStyle} />
      </Box>
      {deviceId && (
        <Box style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Tooltip title='The ack state is managed on device' arrow>
            <Button
              variant='outlined'
              size='small'
              disabled={ackLoading}
              onClick={async () => {
                setAckLoading(true)
                try {
                  await enableAcks({ variables: { device: deviceId } })
                  setAckMessage({ text: 'Acks enabled', severity: 'success' })
                } catch {
                  setAckMessage({ text: 'Failed to enable acks', severity: 'error' })
                }
                setAckLoading(false)
              }}>
              Enable Acks
            </Button>
          </Tooltip>
          <Tooltip title='The ack state is managed on device' arrow>
            <Button
              variant='outlined'
              size='small'
              disabled={ackLoading}
              onClick={async () => {
                setAckLoading(true)
                try {
                  await disableAcks({ variables: { device: deviceId } })
                  setAckMessage({ text: 'Acks disabled', severity: 'success' })
                } catch {
                  setAckMessage({ text: 'Failed to disable acks', severity: 'error' })
                }
                setAckLoading(false)
              }}>
              Disable Acks
            </Button>
          </Tooltip>
        </Box>
      )}
      <Snackbar open={!!ackMessage} autoHideDuration={3000} onClose={() => setAckMessage(null)}>
        <MuiAlert onClose={() => setAckMessage(null)} severity={ackMessage?.severity}>
          {ackMessage?.text}
        </MuiAlert>
      </Snackbar>
      <>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}>
          <Box>
            <Typography variant='h6'>Board Status Log (Yocto3 only)</Typography>
          </Box>
          {viewBoardStatus ? (
            <Box
              style={{
                borderRadius: '50%',
                width: 10,
                height: 10,
                margin: 4,
                backgroundColor:
                  boardStatus?.[0]?.includes(`"online":"Online"`) && boardStatus?.[0]?.includes(`"isOnline":true`)
                    ? 'green'
                    : 'red'
              }}
            />
          ) : (
            <Box
              style={{
                borderRadius: '50%',
                width: 10,
                height: 10,
                margin: 4,
                backgroundColor: 'grey'
              }}
            />
          )}
        </Box>
        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Button
            onClick={() => {
              setBoardStatus([])
            }}
            title='Clear Log'>
            Clear Log
          </Button>
        </Box>
        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Checkbox
            color='primary'
            checked={viewBoardStatus}
            onChange={() => setViewBoardStatus(prev => !prev)}
            name={'View board status websocket'}></Checkbox>
          <Typography variant='caption'>Enable board status websocket logging</Typography>
        </Box>
        <Box style={{ width: '100%', overflowX: 'scroll', maxHeight: 100, overflowY: 'scroll', paddingBottom: 8 }}>
          {boardStatus.map((message: any) => (
            <pre
              style={{
                fontSize: 8,
                padding: 0,
                backgroundColor: message.includes('connected wss')
                  ? '#eeeeee'
                  : message.includes(`"online":"Online"`) && message.includes(`"isOnline":true`)
                  ? '#90EE90'
                  : '#ff7f7f'
              }}>
              {message + '\n'}
            </pre>
          ))}
        </Box>
        <Typography variant='h6'>WebSocket Message Log</Typography>

        <Spacer size='small' />
        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Button
            onClick={() => {
              setMessages([])
            }}
            title='Clear Log'>
            Clear Log
          </Button>
        </Box>

        <Spacer size='small' />
        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Checkbox
            color='primary'
            checked={sendVerboseHeartbeats}
            onChange={() => setSendVerboseHeartbeats(prev => !prev)}
            name={'Send Verbose Heatbeats from Simulator'}></Checkbox>
          <Typography variant='caption'>Send Verbose Heatbeats from Simulator</Typography>
        </Box>
        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Checkbox
            color='primary'
            checked={sendHeartbeats}
            onChange={() => setSendHeartbeats(prev => !prev)}
            name={'Send Heatbeats from Simulator'}></Checkbox>
          <Typography variant='caption'>Send Heatbeats from Simulator</Typography>
        </Box>

        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Checkbox
            color='primary'
            checked={showWebsocketConnectEvents || showAllLogs}
            onChange={() => setShowWebsocketConnectEvents(prev => !prev)}
            name={'Show WebSocket connect events'}></Checkbox>
          <Typography variant='caption'>Show WebSocket connect events</Typography>
        </Box>

        <Box
          style={{
            flexDirection: 'row'
          }}>
          <Checkbox
            color='primary'
            checked={showAllLogs}
            onChange={() => setShowAllLogs(prev => !prev)}
            name={'Show All WebSocket Events'}></Checkbox>
          <Typography variant='caption'>Show All WebSocket Event</Typography>
        </Box>
        {messages?.map((message, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ccc',
              maxWidth: '80vw',
              borderRadius: '4px',
              padding: '4px',
              backgroundColor: message.otherEvent ? '#90EE90' : 'white',
              overflow: 'scroll'
            }}>
            <Typography variant={'body1'}>
              {index} - {message.messageId}
            </Typography>
            <Typography
              variant={message.otherEvent ? 'caption' : 'body1'}
              style={{
                maxWidth: '80vw'
              }}>
              {message.otherEvent ? message.message + ' ' + message.time : message.message}
            </Typography>
            {!message.otherEvent && (
              <>
                <Spacer size='small' />
                <Typography variant='caption'>Received by WebSocket: {message.time}</Typography>
                <Spacer size='small' />
                <Typography variant='caption'>Message Appearance Event Created: {message.created}</Typography>
                <Spacer size='small' />
                <Typography variant='caption'>Message Server Time: {message.serverTime}</Typography>
                <Spacer size='small' />
                <Typography variant='caption'>Display At: {message.displayAt ?? 'null'}</Typography>
                <Spacer size='small' />
                <Typography variant='caption'>Delay Milliseconds: {message.delay}</Typography>
                <Spacer size='small' />
                <Typography variant='caption'>
                  Transition: {message.transition ? JSON.stringify(message.transition) : ''}
                </Typography>
                <Spacer size='small' />
              </>
            )}
          </div>
        ))}
      </>
    </>
  )
}
